import { LitElement, html, css, nothing } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';

import '@awesome.me/webawesome/dist/components/popover/popover.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';
import { LiveAnnouncer } from '../helpers/live-announcer.js';
import '@awesome.me/webawesome/dist/components/button/button.js';

@customElement('ns-upload')
export class NSUpload extends LitElement {
  @query('#upload-input')
  private __uploadInput?: HTMLInputElement;

  @query('.remove-file')
  private __removeFileButton?: HTMLElement;

  @query('#upload-trigger')
  private __uploadTriggerButton?: HTMLElement;

  @state()
  private __fileType?: string[];

  @state()
  private __popUpText?: string;

  @state()
  private __fileName?: string;

  @state()
  private __popUpOpen = false;

  private __announcer = new LiveAnnouncer(this);

  @property({ type: String })
  label?: string;

  static styles = [
    nsSharedStyles,
    nsLayoutStyles,
    css`
      .button-wrapper {
        position: relative;

        z-index: 1;
      }

      input[type='file'] {
        display: none;
      }

      #upload-info,
      .remove-uploaded-file {
        position: absolute;
        top: 0;
        left: 30px;

        height: 100%;
        width: 100%;

        border-radius: var(--wa-border-radius-l);

        z-index: -1;
      }

      #upload-info {
        background-color: var(--ns-theme-grey-mid);
      }

      .remove-uploaded-file,
      .tick {
        background-color: var(--primary-accent-color);
      }

      .tick {
        position: absolute;
        top: 0;
        left: 60px;

        height: 100%;
        width: 100%;

        border-radius: var(--wa-border-radius-l);

        z-index: -2;
      }

      .pop-up,
      .remove-file,
      .tick-mark,
      .cross-mark {
        background: var(--ns-theme-green);

        height: 20px;
        width: 20px;

        border-radius: var(--wa-border-radius-l);
      }

      .remove-file {
        color: var(--ns-theme-black);
        font-size: var(--wa-font-size-m);

        text-align: center;
      }

      .tick-mark img,
      .cross-mark img {
        width: 50%;
      }

      .pop-up img {
        width: 6px;
      }

      wa-popover {
        --max-width: 200px;
        --arrow-size: 0;
      }

      wa-popover::part(body) {
        position: relative;

        background: var(--ns-theme-grey-mid);
        color: var(--ns-theme-black);

        font-size: var(--wa-font-size-xs);

        padding: var(--wa-space-xs) var(--wa-space-xl) var(--wa-space-xs) var(--wa-space-xs);

        border: 1px solid var(--primary-color);
        border-radius: var(--wa-border-radius-l);
      }

      .close-pop-up {
        background: var(--primary-color);
        color: var(--ns-theme-white);

        position: absolute;
        top: 8px;
        right: 8px;

        border: 0;
        border-radius: var(--wa-border-radius-l);

        width: 20px;
        height: 20px;

        font-size: var(--wa-font-size-m);
      }

      .uploaded-file-name {
        background: var(--ns-theme-grey-dark);
        color: var(--ns-theme-white);

        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__uploadInput) throw new Error('Unable to find upload input');
  }

  init(fileType: string[], popUpText: string) {
    if (!fileType || fileType.length < 1) throw new Error('Missing required file type');
    if (!popUpText) throw new Error('Missing required pop up text');

    this.__fileType = fileType;
    this.__popUpText = popUpText;
  }

  public getFile() {
    if (!this.__uploadInput?.files?.[0]) {
      throw new Error('Please select a file to import.');
    }

    return this.__uploadInput.files[0];
  }

  private __uploadFile(ev: CustomEvent) {
    if (!this.__uploadInput) throw new Error('Unable to find upload input');
    if (!this.__fileType) throw new Error('Unable to find file type');

    const target = ev.target as HTMLInputElement;
    if (!target) throw new Error('Unable to find target');
    if (!target.files) throw new Error('Unable to files');

    const [file] = Array.from(target.files);
    if (!file) throw new Error('Missing required file');

    // Layer 1: Check MIME type
    if (!this.__fileType.includes(file.type)) {
      this.__uploadInput.value = '';
      this.dispatchEvent(
        new CustomEvent('upload-outcome', {
          detail: {
            error: this.__popUpText ?? 'That file type is not supported.',
          },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    this.__fileName = file.name;
    this.__announcer.announce(`Selected file: ${file.name}`);
    this.updateComplete.then(() => this.__removeFileButton?.focus());
    this.dispatchEvent(
      new CustomEvent('upload-outcome', {
        detail: {},
        bubbles: true,
        composed: true,
      }),
    );
    //   status.style.color = "#8FFF8F";
    //   status.textContent = `Selected: ${file.name}`;

    // Now proceed to send to your server for sanitization
    // uploadToServer(file);
  }

  private __renderUploadInfo() {
    return html`
      <div id="upload-info">
        <div class="layout horizontal end-justified center h-100">
          <button
            type="button"
            id="upload-info-trigger"
            class="layout horizontal center-center pop-up clickable mr-1"
            aria-label="File requirements info"
            aria-expanded="${this.__popUpOpen}"
          >
            <img alt="" src="/images/icons/question-mark.svg" />
          </button>
        </div>
        <wa-popover
          for="upload-info-trigger"
          placement="right-start"
          @wa-show="${() => (this.__popUpOpen = true)}"
          @wa-hide="${() => (this.__popUpOpen = false)}"
        >
          ${this.__popUpText}
          <button
            type="button"
            class="layout horizontal center-center close-pop-up clickable"
            data-popover="close"
            aria-label="Close"
          >
            &times;
          </button>
        </wa-popover>
      </div>
    `;
  }

  private __renderRemoveUpload() {
    return html`
      <div class="remove-uploaded-file">
        <div class="layout horizontal end-justified center h-100">
          <button
            type="button"
            class="layout horizontal center-center remove-file clickable mr-1"
            @click="${() => {
              if (!this.__uploadInput) throw new Error('Unable to find upload input');
              this.__fileName = '';
              this.__uploadInput.value = '';
              this.__announcer.announce('File removed.');
              this.updateComplete.then(() => this.__uploadTriggerButton?.focus());
            }}"
            aria-label="Remove uploaded file"
          >
            <div class="layout horizontal center-center cross-mark">
              <img alt="" src="/images/icons/times.svg" />
            </div>
          </button>
        </div>
      </div>
      <div class="tick">
        <div class="layout horizontal end-justified center h-100">
          <div class="layout horizontal center-center tick-mark mr-1">
            <img alt="" src="/images/icons/checkmark.svg" />
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="layout horizontal">
        ${this.__announcer.render()}
        <input
          type="file"
          id="upload-input"
          accept="${this.__fileType}"
          @change="${(ev: CustomEvent) => this.__uploadFile(ev)}"
        />
        <div class="button-wrapper">
          ${when(
            this.__fileName,
            () => html`
              <div class="uploaded-file-name">${this.__fileName}</div>
            `,
            () => html`
              <wa-button
                variant="brand"
                size="s"
                type="button"
                id="upload-trigger"
                @click="${() => this.__uploadInput?.click()}"
              >
                ${this.label
                  ? html`
                      <span class="visually-hidden">${this.label}:</span>
                    `
                  : nothing}
                Upload
              </wa-button>
            `,
          )}
          ${when(
            this.__fileName,
            () => this.__renderRemoveUpload(),
            () => this.__renderUploadInfo(),
          )}
        </div>
      </div>
    `;
  }
}
